import { Color4 } from './Color4';
import { DimensionSource } from './DimensionSource';
import { DimensionSourceScale } from './DimensionSourceScale';
import { PIXEL_RATIO } from './globalFunctions';
import { HorizontalAlignment } from './HorizontalAlignment';
import { MultiLineFont } from './MultiLineFont';
import { Rect } from './Rect';
import { RenderDevice } from './RenderDevice';
import { TextSprite } from './TextSprite';
import { Vector2 } from './Vector2';
import { VerticalAlignment } from './VerticalAlignment';

export interface GlowStrokeConfig {
  enabled?: boolean;
  color?: Color4;
  width?: number; // stroke width in px
  softness?: number; // blur radius in px
  opacity?: number; // 0..1
}

export interface ShadowConfig {
  enabled?: boolean;
  color?: Color4;
  offsetX?: number;
  offsetY?: number;
  softness?: number;
  opacity?: number;
  strokeWidth?: number;
}

// New effect types for complex rendering
export enum EffectType {
  Fill = 'fill',
  Gradient = 'gradient',
  OuterGlow = 'outerGlow',
  InnerGlow = 'innerGlow',
  Shadow = 'shadow',
}

export interface BaseEffect {
  id: string;
  type: EffectType;
  enabled?: boolean;
  order: number; // for sorting effects
}

export interface FillEffect extends BaseEffect {
  type: EffectType.Fill;
  color: Color4;
}

export interface GradientEffect extends BaseEffect {
  type: EffectType.Gradient;
  config: GradientFillConfig;
}

export interface TtfGlowEffect extends BaseEffect {
  type: EffectType.OuterGlow | EffectType.InnerGlow;
  config: GlowStrokeConfig;
}

export interface TtfShadowEffect extends BaseEffect {
  type: EffectType.Shadow;
  config: ShadowConfig;
}

export type RenderEffect = FillEffect | GradientEffect | TtfGlowEffect | TtfShadowEffect;

export interface ComplexRenderConfig {
  effects: RenderEffect[];
  letterSpacing?: number;
  lineSpacing?: number;
  canvasScaleX?: number;
  canvasScaleY?: number;
}

export interface TtfRenderComponentConfig {
  fillColor?: Color4;
  fillGradient?: GradientFillConfig;
  outerGlow?: GlowStrokeConfig;
  innerGlow?: GlowStrokeConfig;
  shadow?: ShadowConfig;
  // Optional per-renderer spacing and scaling overrides
  letterSpacing?: number; // px between characters
  lineSpacing?: number; // px between lines (overrides scene object lineSpacing)
  canvasScaleX?: number; // horizontal canvas scale factor
  canvasScaleY?: number; // vertical canvas scale factor
  // New complex effects system
  complexEffects?: ComplexRenderConfig;
  // Autoscale configuration
  dimensionSource?: DimensionSource;
  dimensionSourceScale?: DimensionSourceScale;
  // Canvas padding - adds transparent space around text (applied after autoscale)
  paddingTop?: number; // px of padding at top
  paddingRight?: number; // px of padding at right
  paddingBottom?: number; // px of padding at bottom
  paddingLeft?: number; // px of padding at left
}

export interface GradientStop {
  offset: number; // 0..1
  color: Color4;
}

export interface GradientFillConfig {
  enabled?: boolean;
  type?: 'linear';
  angle?: number; // degrees
  // normalized shift of the gradient line along its direction: -1..1 (0 = centered)
  offset?: number;
  stops?: GradientStop[];
}

export class TtfRenderComponent extends MultiLineFont {
  private readonly config: TtfRenderComponentConfig;

  constructor(config: TtfRenderComponentConfig) {
    super();
    this.config = config;
  }

  public getConfig(): Readonly<TtfRenderComponentConfig> {
    return this.config;
  }

  public build(
    renderDevice: RenderDevice,
    text: string,
    size: Vector2,
    fontFamily: string,
    fontSize: number,
    lineSpacing: number,
    halign: HorizontalAlignment,
    valign: VerticalAlignment,
    multiline: boolean,
    lang: Intl.LocalesArgument,
    letterSpacing?: number,
    canvasScaleX?: number,
    canvasScaleY?: number
  ): TextSprite {
    // Use config lineSpacing if provided, otherwise use scene object lineSpacing
    const effectiveLineSpacing =
      this.config.lineSpacing !== undefined ? this.config.lineSpacing : lineSpacing;

    // Apply autoscaling if configured
    const autoscaleResult = this._applyAutoscale(
      text,
      size,
      fontFamily,
      fontSize,
      effectiveLineSpacing,
      multiline,
      lang
    );
    const adjustedFontSize = autoscaleResult.fontSize;
    const adjustedSize = autoscaleResult.size;

    // Apply padding after autoscale
    const finalSize = this._applyPaddingToSize(adjustedSize);

    // Check if we should use complex effects system
    if (this.config.complexEffects && this.config.complexEffects.effects.length > 0) {
      return this.buildComplex(
        renderDevice,
        text,
        finalSize,
        fontFamily,
        adjustedFontSize,
        effectiveLineSpacing,
        halign,
        valign,
        multiline,
        lang,
        letterSpacing,
        canvasScaleX,
        canvasScaleY
      );
    }

    // Use legacy build method
    return this.buildLegacy(
      renderDevice,
      text,
      finalSize,
      fontFamily,
      adjustedFontSize,
      effectiveLineSpacing,
      halign,
      valign,
      multiline,
      lang,
      letterSpacing,
      canvasScaleX,
      canvasScaleY
    );
  }

  private _applyAutoscale(
    text: string,
    size: Vector2,
    fontFamily: string,
    fontSize: number,
    lineSpacing: number,
    multiline: boolean,
    lang: Intl.LocalesArgument
  ): { fontSize: number; size: Vector2 } {
    // If no autoscale config, return original values
    if (!this.config.dimensionSource || !this.config.dimensionSourceScale) {
      return { fontSize, size };
    }

    // Create temporary canvas to measure text
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;
    ctx.font = `${fontSize}px '${fontFamily}'`;

    // Measure text bounds (include letter spacing in measurement)
    const effectiveLetterSpacing = this.config.letterSpacing ?? 0;
    const linesAndWidth = this._splitLinesAndGetMaxWidth(ctx, size, text, multiline, lang);
    const lines = linesAndWidth[0];
    let maxWidth = linesAndWidth[1];

    // Add letter spacing to width calculation
    if (effectiveLetterSpacing > 0) {
      const maxLineLength = Math.max(...lines.map((line) => line.length));
      if (maxLineLength > 1) {
        maxWidth += (maxLineLength - 1) * effectiveLetterSpacing;
      }
    }

    // Calculate text bounds with proper line spacing (similar to TtfFont logic)
    // Formula: lineCount * fontSize + (lineCount - 1) * lineSpacing
    const textHeight =
      lines.length > 1 ? lines.length * fontSize + (lines.length - 1) * lineSpacing : fontSize;

    // Calculate additional space needed for effects (glow, shadow, etc.)
    const effectsBounds = this._calculateEffectsBounds();
    const totalWidth = maxWidth + effectsBounds.width;
    const totalHeight = textHeight + effectsBounds.height;

    const textBounds = new Vector2(totalWidth, totalHeight);

    // Calculate scale factor based on dimension source
    let scaleFactor = 1.0;

    switch (this.config.dimensionSource) {
      case 'ProportionalSize': {
        const factorX = size.x / textBounds.x;
        const factorY = size.y / textBounds.y;
        scaleFactor = Math.min(factorX, factorY); // Use minimum to fit both dimensions
        break;
      }
      case 'Size': {
        const factorX = size.x / textBounds.x;
        const factorY = size.y / textBounds.y;
        // For Size, we could use different logic, but for now use the same as ProportionalSize
        scaleFactor = Math.min(factorX, factorY);
        break;
      }
      case 'Frame':
      default: {
        // Frame mode - only scale based on width
        scaleFactor = size.x / textBounds.x;
        break;
      }
    }

    // Apply dimension scale constraints
    switch (this.config.dimensionSourceScale) {
      case 'DownOnly':
        if (scaleFactor > 1.0) scaleFactor = 1.0;
        break;
      case 'UpOnly':
        if (scaleFactor < 1.0) scaleFactor = 1.0;
        break;
      case 'Both':
        // No constraints - allow both up and down scaling
        break;
    }

    const adjustedFontSize = fontSize * scaleFactor;

    // Calculate the actual text bounds after scaling
    const scaledTextWidth = textBounds.x * scaleFactor;
    const scaledTextHeight =
      lines.length > 1
        ? lines.length * adjustedFontSize + (lines.length - 1) * lineSpacing
        : adjustedFontSize;

    // For canvas size, we generally keep the original size unless we need to expand
    let adjustedSize = size.clone();
    if (this.config.dimensionSourceScale !== 'DownOnly' && scaleFactor > 1.0) {
      // Only expand canvas if we're scaling up and not in DownOnly mode
      adjustedSize = new Vector2(
        Math.max(size.x, scaledTextWidth),
        Math.max(size.y, scaledTextHeight)
      );
    }

    return { fontSize: adjustedFontSize, size: adjustedSize };
  }

  private _splitLinesAndGetMaxWidth(
    ctx: CanvasRenderingContext2D,
    size: Vector2,
    text: string,
    multiline: boolean,
    lang: Intl.LocalesArgument
  ): [string[], number] {
    let cutWidth = size.x;
    let maxWidth = 0.0;

    // For autoscale measurement, we need to know the natural text size
    // So we don't cut the text when measuring for scaling purposes
    if (this.config.dimensionSource && this.config.dimensionSourceScale) {
      cutWidth = 0.0; // Don't cut text when measuring for autoscale
    }

    const measureText = (text: string): number => {
      const width = ctx.measureText(text).width;
      if (width > maxWidth) {
        maxWidth = width;
      }
      return width;
    };

    let lines: string[];

    if (multiline) {
      lines = this.splitText(measureText, text, cutWidth, lang);
    } else {
      lines = [text];
      maxWidth = ctx.measureText(text).width;
    }

    return [lines, maxWidth];
  }

  private _calculateEffectsBounds(): { width: number; height: number } {
    let extraWidth = 0;
    let extraHeight = 0;

    // Check legacy effects first
    if (this.config.outerGlow?.enabled) {
      const glowWidth = (this.config.outerGlow.width ?? 0) + (this.config.outerGlow.softness ?? 0);
      extraWidth = Math.max(extraWidth, glowWidth * 2); // Both sides
      extraHeight = Math.max(extraHeight, glowWidth * 2); // Top and bottom
    }

    if (this.config.innerGlow?.enabled) {
      const glowWidth = (this.config.innerGlow.width ?? 0) + (this.config.innerGlow.softness ?? 0);
      extraWidth = Math.max(extraWidth, glowWidth * 2);
      extraHeight = Math.max(extraHeight, glowWidth * 2);
    }

    if (this.config.shadow?.enabled) {
      const shadowOffsetX = Math.abs(this.config.shadow.offsetX ?? 0);
      const shadowOffsetY = Math.abs(this.config.shadow.offsetY ?? 0);
      const shadowSoftness = this.config.shadow.softness ?? 0;

      extraWidth = Math.max(extraWidth, shadowOffsetX + shadowSoftness);
      extraHeight = Math.max(extraHeight, shadowOffsetY + shadowSoftness);
    }

    // Check complex effects
    if (this.config.complexEffects?.effects) {
      for (const effect of this.config.complexEffects.effects) {
        if (!effect.enabled) continue;

        switch (effect.type) {
          case EffectType.OuterGlow:
          case EffectType.InnerGlow: {
            const glowEffect = effect as any;
            if (glowEffect.config) {
              const glowWidth = (glowEffect.config.width ?? 0) + (glowEffect.config.softness ?? 0);
              extraWidth = Math.max(extraWidth, glowWidth * 2);
              extraHeight = Math.max(extraHeight, glowWidth * 2);
            }
            break;
          }
          case EffectType.Shadow: {
            const shadowEffect = effect as any;
            if (shadowEffect.config) {
              const shadowOffsetX = Math.abs(shadowEffect.config.offsetX ?? 0);
              const shadowOffsetY = Math.abs(shadowEffect.config.offsetY ?? 0);
              const shadowSoftness = shadowEffect.config.softness ?? 0;
              const shadowStrokeWidth = shadowEffect.config.strokeWidth ?? 0;

              extraWidth = Math.max(extraWidth, shadowOffsetX + shadowSoftness + shadowStrokeWidth);
              extraHeight = Math.max(
                extraHeight,
                shadowOffsetY + shadowSoftness + shadowStrokeWidth
              );
            }
            break;
          }
        }
      }
    }

    return { width: extraWidth, height: extraHeight };
  }

  private _applyPaddingToSize(size: Vector2): Vector2 {
    const paddingTop = this.config.paddingTop ?? 0;
    const paddingRight = this.config.paddingRight ?? 0;
    const paddingBottom = this.config.paddingBottom ?? 0;
    const paddingLeft = this.config.paddingLeft ?? 0;

    return new Vector2(size.x + paddingLeft + paddingRight, size.y + paddingTop + paddingBottom);
  }

  private _getPaddingOffset(): { x: number; y: number } {
    const paddingTop = this.config.paddingTop ?? 0;
    const paddingLeft = this.config.paddingLeft ?? 0;

    return { x: paddingLeft, y: paddingTop };
  }

  private buildLegacy(
    renderDevice: RenderDevice,
    text: string,
    size: Vector2,
    fontFamily: string,
    fontSize: number,
    lineSpacing: number,
    halign: HorizontalAlignment,
    valign: VerticalAlignment,
    multiline: boolean,
    lang: Intl.LocalesArgument,
    letterSpacing?: number,
    canvasScaleX?: number,
    canvasScaleY?: number
  ): TextSprite {
    const dpr = parseFloat(PIXEL_RATIO?.toString() ?? '1.0');
    const canvas = document.createElement('canvas');
    canvas.width = size.x * dpr;
    canvas.height = size.y * dpr;
    const ctx = canvas.getContext('2d')!;

    // Get padding offset for text positioning
    const paddingOffset = this._getPaddingOffset();

    const effectiveLetterSpacing = this.config.letterSpacing ?? letterSpacing ?? 0;
    const sx = this.config.canvasScaleX ?? canvasScaleX ?? 1.0;
    const sy = this.config.canvasScaleY ?? canvasScaleY ?? 1.0;

    // Prepare context
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (dpr !== 1) {
      ctx.scale(dpr, dpr);
    }
    ctx.font = `${fontSize.toString()}px '${fontFamily}'`;
    ctx.textAlign = this._toCanvasTextAlign(halign);
    ctx.textBaseline = this._toCanvasTextBaseline(valign);
    ctx.fontKerning = 'none';
    ctx.textRendering = 'geometricPrecision';

    const lines = multiline ? text.split('\n') : [text];

    // Calculate effective size for text positioning (excluding padding)
    const paddingTop = this.config.paddingTop ?? 0;
    const paddingRight = this.config.paddingRight ?? 0;
    const paddingBottom = this.config.paddingBottom ?? 0;
    const paddingLeft = this.config.paddingLeft ?? 0;

    const effectiveSize = new Vector2(
      size.x - paddingLeft - paddingRight,
      size.y - paddingTop - paddingBottom
    );

    const x = this._getX(effectiveSize, ctx.textAlign as CanvasTextAlign) + paddingOffset.x;
    const yStart = this._getY(ctx, effectiveSize, fontSize, lineSpacing, lines) + paddingOffset.y;

    // Determine fill style (gradient has priority over solid color)
    let fillStyle: string | CanvasGradient = (this.config.fillColor ?? Color4.White).asHtml;
    const grad = this.config.fillGradient;
    if (grad?.enabled && grad.stops && grad.stops.length >= 2) {
      fillStyle = this._createGradient(ctx, size, grad);
    }

    // Shadow behind everything
    if (this.config.shadow?.enabled) {
      this._drawShadow(
        ctx,
        lines,
        x,
        yStart,
        fontSize,
        lineSpacing,
        effectiveLetterSpacing,
        sx,
        sy,
        this.config.shadow,
        0,
        lang
      );
    }

    // Outer glow under fill
    if (this.config.outerGlow?.enabled) {
      this._drawGlow(
        ctx,
        lines,
        x,
        yStart,
        fontSize,
        lineSpacing,
        effectiveLetterSpacing,
        sx,
        sy,
        this.config.outerGlow,
        /*keepInner*/ false,
        lang
      );
    }

    // Fill
    this._drawFill(
      ctx,
      lines,
      x,
      yStart,
      fontSize,
      lineSpacing,
      effectiveLetterSpacing,
      sx,
      sy,
      lang,
      fillStyle
    );

    // Inner glow above fill
    if (this.config.innerGlow?.enabled) {
      this._drawGlow(
        ctx,
        lines,
        x,
        yStart,
        fontSize,
        lineSpacing,
        effectiveLetterSpacing,
        sx,
        sy,
        this.config.innerGlow,
        /*keepInner*/ true,
        lang
      );
    }

    const texture = renderDevice.createTextureFromCanvas(canvas)!;
    return new TextSprite(
      texture,
      new Rect(Vector2.Zero, new Vector2(canvas.width, canvas.height)),
      new Rect(Vector2.Zero, size.clone())
    );
  }

  private buildComplex(
    renderDevice: RenderDevice,
    text: string,
    size: Vector2,
    fontFamily: string,
    fontSize: number,
    lineSpacing: number,
    halign: HorizontalAlignment,
    valign: VerticalAlignment,
    multiline: boolean,
    lang: Intl.LocalesArgument,
    letterSpacing?: number,
    canvasScaleX?: number,
    canvasScaleY?: number
  ): TextSprite {
    const dpr = parseFloat(PIXEL_RATIO?.toString() ?? '1.0');
    const canvas = document.createElement('canvas');
    canvas.width = size.x * dpr;
    canvas.height = size.y * dpr;
    const ctx = canvas.getContext('2d')!;

    const complexConfig = this.config.complexEffects!;

    // Get padding offset for text positioning
    const paddingOffset = this._getPaddingOffset();

    const effectiveLetterSpacing = complexConfig.letterSpacing ?? letterSpacing ?? 0;
    const sx = complexConfig.canvasScaleX ?? canvasScaleX ?? 1.0;
    const sy = complexConfig.canvasScaleY ?? canvasScaleY ?? 1.0;

    // Prepare context
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (dpr !== 1) {
      ctx.scale(dpr, dpr);
    }
    ctx.font = `${fontSize.toString()}px '${fontFamily}'`;
    ctx.textAlign = this._toCanvasTextAlign(halign);
    ctx.textBaseline = this._toCanvasTextBaseline(valign);

    const lines = multiline ? text.split('\n') : [text];

    // Calculate effective size for text positioning (excluding padding)
    const paddingTop = this.config.paddingTop ?? 0;
    const paddingRight = this.config.paddingRight ?? 0;
    const paddingBottom = this.config.paddingBottom ?? 0;
    const paddingLeft = this.config.paddingLeft ?? 0;

    const effectiveSize = new Vector2(
      size.x - paddingLeft - paddingRight,
      size.y - paddingTop - paddingBottom
    );

    const x = this._getX(effectiveSize, ctx.textAlign as CanvasTextAlign) + paddingOffset.x;
    const yStart = this._getY(ctx, effectiveSize, fontSize, lineSpacing, lines) + paddingOffset.y;

    // Sort effects by order
    const sortedEffects = [...complexConfig.effects]
      .filter((effect) => effect.enabled !== false)
      .sort((a, b) => a.order - b.order);

    // Apply effects in order
    for (const effect of sortedEffects) {
      this._applyEffect(
        ctx,
        effect,
        lines,
        x,
        yStart,
        fontSize,
        lineSpacing,
        effectiveLetterSpacing,
        sx,
        sy,
        size,
        lang
      );
    }

    const texture = renderDevice.createTextureFromCanvas(canvas)!;
    return new TextSprite(
      texture,
      new Rect(Vector2.Zero, new Vector2(canvas.width, canvas.height)),
      new Rect(Vector2.Zero, size.clone())
    );
  }

  private _applyEffect(
    ctx: CanvasRenderingContext2D,
    effect: RenderEffect,
    lines: string[],
    x: number,
    yStart: number,
    fontSize: number,
    lineSpacing: number,
    letterSpacing: number,
    sx: number,
    sy: number,
    size: Vector2,
    lang: Intl.LocalesArgument
  ): void {
    switch (effect.type) {
      case EffectType.Fill:
        const fillEffect = effect as FillEffect;
        const fillColor = fillEffect.color instanceof Color4 ? fillEffect.color.asHtml : '#ffffff';
        this._drawFill(
          ctx,
          lines,
          x,
          yStart,
          fontSize,
          lineSpacing,
          letterSpacing,
          sx,
          sy,
          lang,
          fillColor
        );
        break;

      case EffectType.Gradient:
        const gradientEffect = effect as GradientEffect;
        if (
          gradientEffect.config.enabled !== false &&
          gradientEffect.config.stops &&
          gradientEffect.config.stops.length >= 2
        ) {
          const gradientStyle = this._createGradient(ctx, size, gradientEffect.config);
          this._drawFill(
            ctx,
            lines,
            x,
            yStart,
            fontSize,
            lineSpacing,
            letterSpacing,
            sx,
            sy,
            lang,
            gradientStyle
          );
        }
        break;

      case EffectType.Shadow:
        const shadowEffect = effect as TtfShadowEffect;
        if (shadowEffect.config.enabled !== false) {
          const strokeWidth = shadowEffect.config.strokeWidth ?? 0;
          this._drawShadow(
            ctx,
            lines,
            x,
            yStart,
            fontSize,
            lineSpacing,
            letterSpacing,
            sx,
            sy,
            shadowEffect.config,
            strokeWidth,
            lang
          );
        }
        break;

      case EffectType.OuterGlow:
        const outerGlowEffect = effect as TtfGlowEffect;
        if (outerGlowEffect.config.enabled !== false) {
          this._drawGlow(
            ctx,
            lines,
            x,
            yStart,
            fontSize,
            lineSpacing,
            letterSpacing,
            sx,
            sy,
            outerGlowEffect.config,
            false,
            lang
          );
        }
        break;

      case EffectType.InnerGlow:
        const innerGlowEffect = effect as TtfGlowEffect;
        if (innerGlowEffect.config.enabled !== false) {
          this._drawGlow(
            ctx,
            lines,
            x,
            yStart,
            fontSize,
            lineSpacing,
            letterSpacing,
            sx,
            sy,
            innerGlowEffect.config,
            true,
            lang
          );
        }
        break;
    }
  }

  private _drawFill(
    ctx: CanvasRenderingContext2D,
    lines: string[],
    x: number,
    yStart: number,
    fontSize: number,
    lineSpacing: number,
    letterSpacing: number,
    sx: number,
    sy: number,
    lang: Intl.LocalesArgument,
    fillStyle?: string | CanvasGradient
  ): void {
    ctx.save();
    ctx.fillStyle = fillStyle ?? Color4.White.asHtml;
    for (let i = 0; i < lines.length; i++) {
      const y = yStart + i * (fontSize + lineSpacing);
      ctx.save();
      ctx.translate(x, y);
      if (sx !== 1.0 || sy !== 1.0) {
        ctx.scale(sx, sy);
      }
      this._drawTextWithSpacing(ctx, lines[i], 0, 0, letterSpacing, 'fill', lang);
      ctx.restore();
    }
    ctx.restore();
  }

  private _createGradient(
    ctx: CanvasRenderingContext2D,
    size: Vector2,
    grad: GradientFillConfig
  ): CanvasGradient {
    const angleDeg = grad.angle ?? 0; // 0° → left-to-right
    const angleRad = (angleDeg * Math.PI) / 180.0;
    const dirX = Math.cos(angleRad);
    const dirY = Math.sin(angleRad);

    const cx = size.x / 2;
    const cy = size.y / 2;
    const halfLength = Math.abs(dirX) * (size.x / 2) + Math.abs(dirY) * (size.y / 2);
    const normalizedOffset = Math.max(-1, Math.min(1, grad.offset ?? 0));
    const shift = normalizedOffset * halfLength;
    const x0 = cx - dirX * halfLength + dirX * shift;
    const y0 = cy - dirY * halfLength + dirY * shift;
    const x1 = cx + dirX * halfLength + dirX * shift;
    const y1 = cy + dirY * halfLength + dirY * shift;

    const g = ctx.createLinearGradient(x0, y0, x1, y1);
    const stops = grad.stops ?? [];
    for (const stop of stops) {
      const o = Math.max(0, Math.min(1, stop.offset));
      const stopColor = stop.color instanceof Color4 ? stop.color.asHtml : '#ffffff';
      g.addColorStop(o, stopColor);
    }
    return g;
  }

  private _drawShadow(
    ctx: CanvasRenderingContext2D,
    lines: string[],
    x: number,
    yStart: number,
    fontSize: number,
    lineSpacing: number,
    letterSpacing: number,
    sx: number,
    sy: number,
    shadow: ShadowConfig,
    strokeWidth: number = 0,
    lang: Intl.LocalesArgument
  ): void {
    const color = shadow.color ?? Color4.Black;
    // Ensure color is a Color4 object
    const color4 = color instanceof Color4 ? color : Color4.Black;
    const offsetX = shadow.offsetX ?? 0;
    const offsetY = shadow.offsetY ?? 0;
    const softness = Math.max(0, shadow.softness ?? 0);
    const alpha = shadow.opacity ?? 1.0;
    if (alpha <= 0) {
      return;
    }

    // Offscreen layer equal to main canvas size
    const off = document.createElement('canvas');
    off.width = ctx.canvas.width;
    off.height = ctx.canvas.height;
    const octx = off.getContext('2d')!;
    octx.font = ctx.font;
    octx.textAlign = ctx.textAlign as CanvasTextAlign;
    octx.textBaseline = ctx.textBaseline as CanvasTextBaseline;
    octx.fillStyle = color4.asHtml;
    octx.globalAlpha = alpha;
    octx.fontKerning = ctx.fontKerning;
    octx.textRendering = ctx.textRendering;
    for (let i = 0; i < lines.length; i++) {
      const y = yStart + i * (fontSize + lineSpacing);
      octx.save();
      octx.translate(x + offsetX, y + offsetY);
      if (sx !== 1.0 || sy !== 1.0) octx.scale(sx, sy);
      this._drawTextWithSpacing(octx, lines[i], 0, 0, letterSpacing, 'fill', lang);

      if (strokeWidth > 0) {
        octx.lineWidth = strokeWidth;
        octx.strokeStyle = color4.asHtml;
        octx.lineJoin = 'round';
        octx.lineCap = 'round';
        this._drawTextWithSpacing(octx, lines[i], 0, 0, letterSpacing, 'stroke', lang);
      }

      octx.restore();
    }

    // Composite with blur onto main
    ctx.save();
    (ctx as any).filter = softness > 0 ? `blur(${softness}px)` : 'none';
    ctx.drawImage(off, 0, 0);
    (ctx as any).filter = 'none';
    ctx.restore();
  }

  private _drawGlow(
    ctx: CanvasRenderingContext2D,
    lines: string[],
    x: number,
    yStart: number,
    fontSize: number,
    lineSpacing: number,
    letterSpacing: number,
    sx: number,
    sy: number,
    glow: GlowStrokeConfig,
    keepInner: boolean,
    lang: Intl.LocalesArgument
  ): void {
    const color = glow.color ?? Color4.White;
    // Ensure color is a Color4 object
    const color4 = color instanceof Color4 ? color : Color4.White;
    const strokeWidth = Math.max(0, glow.width ?? 0);
    const softness = Math.max(0, glow.softness ?? 0);
    const alpha = glow.opacity ?? 1.0;
    if (strokeWidth <= 0 || alpha <= 0) {
      return;
    }

    // Offscreen layers the size of the main canvas to avoid coordinate issues
    const off = document.createElement('canvas');
    off.width = ctx.canvas.width;
    off.height = ctx.canvas.height;
    const octx = off.getContext('2d')!;
    octx.font = ctx.font;
    octx.textAlign = ctx.textAlign as CanvasTextAlign;
    octx.textBaseline = ctx.textBaseline as CanvasTextBaseline;
    octx.strokeStyle = color4.asHtml;
    octx.lineWidth = strokeWidth;
    octx.lineJoin = 'round';
    octx.lineCap = 'round';
    octx.fontKerning = ctx.fontKerning;
    octx.textRendering = ctx.textRendering;
    octx.globalAlpha = alpha;
    for (let i = 0; i < lines.length; i++) {
      const y = yStart + i * (fontSize + lineSpacing);
      octx.save();
      octx.translate(x, y);
      if (sx !== 1.0 || sy !== 1.0) octx.scale(sx, sy);
      this._drawTextWithSpacing(octx, lines[i], 0, 0, letterSpacing, 'stroke', lang);
      octx.restore();
    }

    const mask = document.createElement('canvas');
    mask.width = off.width;
    mask.height = off.height;
    const mctx = mask.getContext('2d')!;
    mctx.font = ctx.font;
    mctx.textAlign = ctx.textAlign as CanvasTextAlign;
    mctx.textBaseline = ctx.textBaseline as CanvasTextBaseline;
    mctx.fillStyle = '#fff';
    mctx.fontKerning = ctx.fontKerning;
    mctx.textRendering = ctx.textRendering;
    for (let i = 0; i < lines.length; i++) {
      const y = yStart + i * (fontSize + lineSpacing);
      mctx.save();
      mctx.translate(x, y);
      if (sx !== 1.0 || sy !== 1.0) mctx.scale(sx, sy);
      this._drawTextWithSpacing(mctx, lines[i], 0, 0, letterSpacing, 'fill', lang);
      mctx.restore();
    }

    const composed = document.createElement('canvas');
    composed.width = off.width;
    composed.height = off.height;
    const cctx = composed.getContext('2d')!;
    cctx.drawImage(off, 0, 0);
    cctx.globalCompositeOperation = keepInner ? 'destination-in' : 'destination-out';
    cctx.drawImage(mask, 0, 0);
    cctx.globalCompositeOperation = 'source-over';

    ctx.save();
    (ctx as any).filter = softness > 0 ? `blur(${softness}px)` : 'none';
    ctx.drawImage(composed, 0, 0);
    (ctx as any).filter = 'none';
    ctx.restore();
  }

  private _measureTextWidthWithSpacing(
    ctx: CanvasRenderingContext2D,
    text: string,
    letterSpacing: number
  ): number {
    if (!text) return 0;
    const baseWidth = ctx.measureText(text).width;
    const extra = Math.max(0, text.length - 1) * letterSpacing;
    return baseWidth + extra;
  }

  private _drawTextWithSpacing(
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    letterSpacing: number,
    mode: 'fill' | 'stroke',
    lang: Intl.LocalesArgument
  ): void {
    // Fast path if no spacing requested
    if (!text || Math.abs(letterSpacing) < 0.001) {
      if (mode === 'fill') {
        ctx.fillText(text, x, y);
      } else {
        ctx.strokeText(text, x, y);
      }
      return;
    }

    // Compute starting X based on current alignment and total width with spacing.
    const previousAlign = ctx.textAlign as CanvasTextAlign;
    const align = previousAlign;
    let cursorX = x;
    if (align === 'center' || align === 'right') {
      const totalWidth = this._measureTextWidthWithSpacing(ctx, text, letterSpacing);
      if (align === 'center') {
        cursorX -= totalWidth * 0.5;
      } else if (align === 'right') {
        cursorX -= totalWidth;
      }
    }

    // While drawing per-character, force left alignment so each character
    // starts at the given x without additional per-glyph alignment offsets.
    ctx.textAlign = 'left';

    // Draw character by character to apply spacing
    let chars: string[];
    try {
      chars = [...new Intl.Segmenter(lang, { granularity: 'grapheme' }).segment(text)].map(
        (item) => item.segment
      );
    } catch {
      chars = Array.from(text);
    }

    for (const ch of chars) {
      if (mode === 'fill') {
        ctx.fillText(ch, cursorX, y);
      } else {
        ctx.strokeText(ch, cursorX, y);
      }
      cursorX += ctx.measureText(ch).width + letterSpacing;
    }

    // Restore alignment
    ctx.textAlign = previousAlign;
  }

  private _toCanvasTextAlign(halign: HorizontalAlignment): CanvasTextAlign {
    switch (halign) {
      case HorizontalAlignment.Center:
        return 'center';
      case HorizontalAlignment.Right:
        return 'right';
      default:
        return 'left';
    }
  }

  private _toCanvasTextBaseline(valign: VerticalAlignment): CanvasTextBaseline {
    switch (valign) {
      case VerticalAlignment.Center:
        return 'middle';
      case VerticalAlignment.Bottom:
        return 'bottom';
      default:
        return 'top';
    }
  }

  private _getX(size: Vector2, align: CanvasTextAlign): number {
    switch (align) {
      case 'center':
        return size.x / 2;
      case 'right':
        return size.x;
      default:
        return 0;
    }
  }

  private _getY(
    context: CanvasRenderingContext2D,
    size: Vector2,
    fontSize: number,
    lineSpacing: number,
    lines: string[]
  ): number {
    if (context.textBaseline === 'middle') {
      context.textBaseline = 'alphabetic';

      let maxAscent = 0;
      let maxDescent = 0;

      lines.forEach((line) => {
        const metrics = context.measureText(line);
        const ascent = metrics.actualBoundingBoxAscent;
        const descent = metrics.actualBoundingBoxDescent;

        maxAscent = Math.max(maxAscent, ascent);
        maxDescent = Math.max(maxDescent, descent);
      });

      const totalTextHeight = lines.length * fontSize + (lines.length - 1) * lineSpacing;

      const containerCenter = size.y * 0.5;
      return containerCenter - totalTextHeight * 0.5 + maxAscent;
    }

    if (context.textBaseline === 'bottom') {
      let maxAscent = 0;
      let maxDescent = 0;

      lines.forEach((line) => {
        const metrics = context.measureText(line);
        const ascent = metrics.actualBoundingBoxAscent;
        const descent = metrics.actualBoundingBoxDescent - metrics.ideographicBaseline;

        maxAscent = Math.max(maxAscent, ascent);
        maxDescent = Math.max(maxDescent, descent);
      });

      const totalTextHeight = lines.length * fontSize + (lines.length - 1) * lineSpacing;

      const lastLineBaseline = size.y - maxDescent;
      const firstLineY = lastLineBaseline - (totalTextHeight - fontSize);

      return firstLineY;
    }

    context.textBaseline = 'alphabetic';

    let maxAscent = 0;

    lines.forEach((line) => {
      const metrics = context.measureText(line);
      const ascent = metrics.actualBoundingBoxAscent;
      maxAscent = Math.max(maxAscent, ascent);
    });

    return maxAscent;
  }
}
